import { DOCUMENT, Injectable, inject } from '@angular/core';
import { TUI_IS_MOBILE } from '@taiga-ui/cdk';
import { debounceTime, fromEvent, merge } from 'rxjs';

import { DROPDOWN_MOBILE_CLASS } from './mobile-dropdown-ghost-click.service';

// Поля, для которых телефон показывает клавиатуру
const EDITABLE =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([readonly]), textarea, [contenteditable="true"]';

// Отступ от краёв видимой области: поле не должно упираться в клавиатуру или шапку формы
const MARGIN_PX = 56;

// Полноэкранный список tuiDropdownMobile (без заголовка — не шторка)
const FULLSCREEN_LIST = 'tui-dropdown-mobile:not(._sheet)';

// Экранная клавиатура на телефоне:
// - поле в фокусе не прячется под ней. Формы живут во внутренних прокрутках (SidePage,
//   tui-scrollbar) при неподвижной странице, и браузер, особенно Safari на iOS, не всегда
//   докручивает их сам. После фокуса и после каждого изменения видимой области поле
//   прокручивается в центр видимой части;
// - полноэкранный список tuiDropdownMobile доходит до низа видимой области. Taiga считает
//   его высоту под открытую клавиатуру и пересчитывает только при изменении размера поля,
//   поэтому после скрытия клавиатуры список оставался на полэкрана;
// - поле полноэкранного списка не теряет фокус. Долгое нажатие переводит его на опцию (тап —
//   нет, у tui-data-list mousedown.prevent), Taiga сбрасывает сдвиг страницы, и поле уходит
//   под список. Фокус возвращается сразу, а события перехвачены, чтобы blur не стёр текст поиска
@Injectable({ providedIn: 'root' })
export class MobileKeyboardService {
  private readonly doc = inject(DOCUMENT);

  // Идёт возврат фокуса в поле: его focus/focusin обработчикам формы не показываем
  private restoringFocus = false;

  constructor() {
    const win = this.doc.defaultView;
    if (!inject(TUI_IS_MOBILE) || !win) return;

    const viewport = win.visualViewport;
    const viewportResize$ = viewport ? fromEvent(viewport, 'resize') : fromEvent(win, 'resize');

    merge(fromEvent(this.doc, 'focusin'), viewportResize$)
      // Клавиатура выезжает с анимацией, высота видимой области меняется несколько раз
      .pipe(debounceTime(150))
      .subscribe(() => this.revealFocused(win));

    // Без задержки: список тянется за клавиатурой вместе с её анимацией
    viewportResize$.subscribe(() => this.fitFullscreenList(win));

    // window и capture — раньше обработчиков поля и Taiga
    const onFocusLeave = (event: Event) => this.keepListFieldFocused(event as FocusEvent);
    const onFocusReturn = (event: Event) => {
      if (this.restoringFocus) event.stopImmediatePropagation();
    };

    win.addEventListener('blur', onFocusLeave, true);
    win.addEventListener('focusout', onFocusLeave, true);
    win.addEventListener('focus', onFocusReturn, true);
    win.addEventListener('focusin', onFocusReturn, true);
  }

  private keepListFieldFocused(event: FocusEvent): void {
    const field = event.target;
    const next = event.relatedTarget;

    if (
      !(field instanceof HTMLElement) ||
      !(next instanceof HTMLElement) ||
      !field.matches(EDITABLE) ||
      field.closest(FULLSCREEN_LIST) ||
      !next.closest(FULLSCREEN_LIST) ||
      next.matches(EDITABLE)
    ) {
      return;
    }

    event.stopImmediatePropagation();

    // blur идёт раньше focusout: возвращаем фокус на последнем из них
    if (event.type === 'focusout') {
      this.restoringFocus = true;
      field.focus({ preventScroll: true });
      this.restoringFocus = false;
    }
  }

  private fitFullscreenList(win: Window): void {
    const list = this.doc.querySelector<HTMLElement>(FULLSCREEN_LIST);

    // Высоту задаёт Taiga, пока поле в фокусе; без неё список и так на весь экран
    if (!list?.style.height) return;

    const viewport = win.visualViewport;
    const bottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? win.innerHeight);

    // top — из вычисленного стиля: rect во время анимации появления сдвинут transform
    const top = parseFloat(win.getComputedStyle(list).top) || 0;

    list.style.height = `${Math.max(bottom - top, 0)}px`;
  }

  private revealFocused(win: Window): void {
    const focused = this.doc.activeElement;

    if (
      !(focused instanceof HTMLElement) ||
      !focused.matches(EDITABLE) ||
      // Полноэкранный список tuiDropdownMobile держит поле над клавиатурой сам
      this.doc.body.classList.contains(DROPDOWN_MOBILE_CLASS)
    ) {
      return;
    }

    const viewport = win.visualViewport;
    const top = viewport?.offsetTop ?? 0;
    const bottom = top + (viewport?.height ?? win.innerHeight);
    const rect = (
      focused.closest('tui-textfield, tui-textarea') ?? focused
    ).getBoundingClientRect();

    if (rect.top < top + MARGIN_PX || rect.bottom > bottom - MARGIN_PX) {
      focused.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}
